import { NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Event, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { AppStorage } from '@app/common/services/data-api/app-storage.service';
import { MenuService } from '@app/common/services/menu.service';
import { DefaultVersionComponent } from '@app/shared/components/default-version/default-version.component';
import { DialogContainerDirective } from '@progress/kendo-angular-dialog';
import { Scenario } from 'app/shared/models';
import { MenuItem } from 'app/shared/models/menu-item';
import { ThemeObject } from 'environments/env-name';
import PerfectScrollbar from 'perfect-scrollbar';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SidebarComponent } from '../components/sidebar/sidebar.component';
import { EnvThemeService } from '../services/env-theme.service';

@Component({
  selector: 'app-layout',
  templateUrl: './default-layout.component.html',
  styleUrls: ['./default-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DefaultVersionComponent,
    DialogContainerDirective,
    NgClass,
    RouterOutlet,
    SidebarComponent,
  ],
})
export class DefaultLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  private menuService = inject(MenuService);
  private router = inject(Router);
  private appStore = inject(AppStorage);
  private envThemeService = inject(EnvThemeService);

  public menuItems: MenuItem[];
  public selectedState: string = '';
  public parentState: string;
  public displaySubMenu = true;
  public subMenuItems: MenuItem[] = [];
  public subscription: Subscription;
  public url: string;
  public searchBarDisplay: string = 'none';
  public inLiveScenario = false;
  public loadingDisplayed: boolean = false;
  public envName: string;
  public bgColor1: {};
  public bgColor2: {};
  public envTheme: ThemeObject;

  private scenarioSubscription: Subscription;

  constructor() {
    this.menuService.getAllAsync().subscribe(m => {
      this.menuItems = m;
    });

    this.router.events.subscribe((event: Event) => {
      this.navigationInterceptor(event);
    });
  }

  navigationInterceptor(event: Event): void {
    if (event instanceof NavigationStart) {
      this.loadingDisplayed = true;
    }

    if (event instanceof NavigationEnd) {
      this.loadingDisplayed = false;
    }
  }

  sideBarPs: PerfectScrollbar;
  sideSubBarPs: PerfectScrollbar;
  contentPs: PerfectScrollbar;

  ngOnInit() {
    this.envTheme = this.envThemeService.getEnvStyle();
    this.envName = this.envTheme.envName;
    if (this.envTheme.themeColors.length > 1) {
      this.bgColor2 = this.envTheme.themeColors[1];
    } else {
      this.bgColor2 = null;
    }

    this.inLiveScenario = this.appStore.knownStore.scenario && this.appStore.knownStore.scenario.IsLiveScenario;
    this.scenarioSubscription = this.appStore.knownEvents.scenarioSelected
      .subscribe((s: Scenario) => {
        this.inLiveScenario = s && s.IsLiveScenario;
      });
    let menuItems = this.menuService.getCurrentState();

    if (menuItems.length > 0 && menuItems[0]) {
      this.selectedState = menuItems[0].state;
      this.subMenuItems = menuItems[0].children ? menuItems[0].children : null;
    }

    if (!this.parentState) {
      this.parentState = this.selectedState;
    }
  }

  ngAfterViewInit(): void {
    const elemSidebar = <HTMLElement>document.querySelector('.app-inner > .sidebar-panel-narrow');
    const elemSubSidebar = <HTMLElement>document.querySelector('.app-inner > .sidebar-panel');
    const elemContent = <HTMLElement>document.querySelector('.app-inner > .mat-sidenav-content');

    if (window.matchMedia(`(min-width: 960px)`).matches && !this.isMac()) {
      if (elemSidebar) { this.sideBarPs = new PerfectScrollbar(elemSidebar, { wheelSpeed: 2, suppressScrollX: true }); }
      if (elemSubSidebar) { this.sideSubBarPs = new PerfectScrollbar(elemSubSidebar, { wheelSpeed: 2, suppressScrollX: true }); }
      if (elemContent) { this.contentPs = new PerfectScrollbar(elemContent, { wheelSpeed: 2, suppressScrollX: true }); }
    }

    this.subscription = this.router.events.pipe(
      filter(
        (event: Event, index: number) =>
          event instanceof NavigationEnd
      ))
      .subscribe((event: NavigationEnd) => {

        this.url = event.url;
        if (window.matchMedia(`(min-width: 960px)`).matches && !this.isMac()) {
          if (elemSidebar) { this.sideBarPs.update(); }
          if (elemSubSidebar) { this.sideSubBarPs.update(); }
          if (elemContent) { this.contentPs.update(); }
        }
      }
      );
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.scenarioSubscription) {
      this.scenarioSubscription.unsubscribe();
    }
  }

  menuItemClick(item: MenuItem): void {
    if (!item.children) {
      this.router.navigateByUrl(item.state);
    }

    this.parentState = item.state;
    this.subMenuItems = item.children;
    this.displaySubMenu = true;
  }

  searchToggle(): void {
    if (this.searchBarDisplay === 'none') {
      this.searchBarDisplay = 'block';
    } else {
      this.searchBarDisplay = 'none';
    }
  }

  isOver(): boolean {
    return window.matchMedia(`(max-width: 960px)`).matches;
  }

  isMac(): boolean {
    return navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  }
}